/**
 * VÉRAULT Portal — Today's Desk + What Changed This Week (Phase 1.4)
 *
 * Two read-only surfaces that share a calm, executive aesthetic:
 *   • TodaysDesk        — "what needs me" feed for the Concierge Desk.
 *   • WhatChangedCard   — 7-day audit summary for the Advisor Overview.
 *
 * Both are pure renderers on top of /api/advisor/desk/*. No mutations,
 * deep-link to source records.
 */
import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Inbox, Clock, MessageSquare, Archive, CalendarDays,
  AlertCircle, Loader2, ChevronRight, Sparkles,
} from "lucide-react";
import { Surface, api, motion as portalMotion } from "../../index";

const { fadeInUp, stagger } = portalMotion;

const SECTION_META = {
  new:           { label: "New / Needs Review", icon: Inbox },
  waiting_on_me: { label: "Waiting on Me",      icon: Clock },
  client_care:   { label: "Client Care",        icon: MessageSquare },
  auto_archive:  { label: "Auto-Archive Notices", icon: Archive },
  upcoming:      { label: "Upcoming",           icon: CalendarDays },
};

function fmtRel(iso) {
  if (!iso) return null;
  try {
    const dt = new Date(iso);
    return dt.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  } catch { return null; }
}

function DeskItem({ item }) {
  const content = (
    <div className="flex items-start gap-3 px-3 py-2.5 hover:bg-[hsl(var(--background-paper))]/60 transition-colors">
      <div className="flex-1 min-w-0">
        <p className="font-serif text-[14px] text-[hsl(var(--foreground))] truncate">{item.title}</p>
        <p className="font-accent italic text-[12px] text-[hsl(var(--foreground-muted))] truncate">
          {item.subtitle || fmtRel(item.created_at)}
        </p>
      </div>
      {item.urgent && <AlertCircle className="size-3.5 text-[hsl(var(--accent-terracotta))] shrink-0 mt-1" strokeWidth={1.5} />}
      {item.href && <ChevronRight className="size-3.5 text-[hsl(var(--foreground-muted))] shrink-0 mt-1" strokeWidth={1.5} />}
    </div>
  );
  return item.href ? (
    <Link to={item.href} data-testid={`desk-item-${item.kind}-${item.id}`}>{content}</Link>
  ) : (
    <div data-testid={`desk-item-${item.kind}-${item.id}`}>{content}</div>
  );
}

export function TodaysDesk() {
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    api.get("/advisor/desk/today")
      .then(({ data: d }) => setData(d))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <Surface variant="paper" padded data-testid="todays-desk-loading">
        <div className="flex items-center justify-center py-6">
          <Loader2 className="size-4 text-[hsl(var(--foreground-muted))] animate-spin" strokeWidth={1.5} />
        </div>
      </Surface>
    );
  }
  if (!data) return null;

  const sections = data.sections || {};
  if ((data.total || 0) === 0) {
    return (
      <Surface variant="paper" padded data-testid="todays-desk-card">
        <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-[hsl(var(--accent-gold))]">Today&apos;s Desk</p>
        <p className="mt-2 font-accent italic text-[14px] text-[hsl(var(--foreground-muted))]">
          A calm desk. Nothing waiting on you right now.
        </p>
      </Surface>
    );
  }

  return (
    <motion.div initial="initial" animate="animate" variants={stagger} className="space-y-3" data-testid="todays-desk-card">
      <motion.div variants={fadeInUp}>
        <Surface variant="paper" padded>
          <div className="flex items-baseline justify-between">
            <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-[hsl(var(--accent-gold))]">Today&apos;s Desk</p>
            <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))]">
              {data.total} item{data.total === 1 ? "" : "s"} need you
            </p>
          </div>
        </Surface>
      </motion.div>

      {Object.entries(SECTION_META).map(([key, meta]) => {
        const items = sections[key] || [];
        if (items.length === 0) return null;
        const Icon = meta.icon;
        return (
          <motion.div key={key} variants={fadeInUp}>
            <Surface variant="default" padded={false} data-testid={`desk-section-${key}`}>
              <div className="px-3 py-2 flex items-center gap-2 border-b border-[rgba(0,0,0,0.05)]">
                <Icon className="size-3.5 text-[hsl(var(--foreground-muted))]" strokeWidth={1.5} />
                <p className="font-serif text-[14px] text-[hsl(var(--foreground))]">{meta.label}</p>
                <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))]">· {items.length}</p>
              </div>
              <ul className="divide-y divide-[rgba(0,0,0,0.04)]">
                {items.map((it) => <li key={it.id}><DeskItem item={it} /></li>)}
              </ul>
            </Surface>
          </motion.div>
        );
      })}
    </motion.div>
  );
}

const ACTION_LABEL = {
  attach: "attached", submit: "submitted", sign: "signed", upload: "uploaded",
  update: "updated", archive: "archived", visibility_change: "visibility changed",
  restore: "restored", send: "sent", complete: "completed",
};

export function WhatChangedCard() {
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    api.get("/advisor/desk/this-week")
      .then(({ data: d }) => setData(d))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <Surface variant="paper" padded data-testid="what-changed-loading">
        <div className="flex items-center justify-center py-4">
          <Loader2 className="size-4 text-[hsl(var(--foreground-muted))] animate-spin" strokeWidth={1.5} />
        </div>
      </Surface>
    );
  }
  if (!data) return null;

  const noChanges = (data.total_changes || 0) === 0 && (data.email_log?.total || 0) === 0;
  return (
    <Surface variant="paper" padded data-testid="what-changed-card">
      <div className="flex items-start gap-3">
        <Sparkles className="size-4 text-[hsl(var(--accent-gold))] mt-0.5 shrink-0" strokeWidth={1.5} />
        <div className="flex-1 min-w-0">
          <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-[hsl(var(--accent-gold))]">
            What Changed This Week
          </p>
          {noChanges ? (
            <p className="mt-2 font-accent italic text-[14px] text-[hsl(var(--foreground-muted))]">
              A quiet week so far. Nothing to surface.
            </p>
          ) : (
            <>
              <p className="mt-1 font-serif text-[15px] text-[hsl(var(--foreground))]">
                {data.total_changes} record change{data.total_changes === 1 ? "" : "s"}
                {" · "}{data.email_log?.total || 0} email{data.email_log?.total === 1 ? "" : "s"} logged
              </p>
              {Object.keys(data.by_action || {}).length > 0 && (
                <div className="mt-2 flex flex-wrap gap-2" data-testid="what-changed-actions">
                  {Object.entries(data.by_action).map(([action, count]) => (
                    <span
                      key={action}
                      className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-white border border-[rgba(0,0,0,0.06)] font-mono text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--foreground-muted))]"
                    >
                      <span className="text-[hsl(var(--foreground))]">{count}</span>
                      {ACTION_LABEL[action] || action.replace(/_/g, " ")}
                    </span>
                  ))}
                </div>
              )}
              {data.orphan_warnings > 0 && (
                <p className="mt-2 font-accent italic text-[12px] text-[hsl(var(--accent-gold))]" data-testid="what-changed-orphans">
                  <AlertCircle className="size-3 inline mr-1" strokeWidth={1.5} />
                  {data.orphan_warnings} record{data.orphan_warnings === 1 ? "" : "s"} flagged by the orphan check.
                  {" "}
                  <Link to="/portal/advisor/intelligence" className="underline decoration-[hsl(var(--accent-gold))]/40 underline-offset-2">
                    Look closer
                  </Link>
                </p>
              )}
            </>
          )}
        </div>
      </div>
    </Surface>
  );
}
